import {
  getFetchValues,
  getFilterAndBookingType,
  getFilterEntries,
  getFilterParams
} from "../../../components/Reports/CapacityFilter/FilterSlideout/functions/handleGroupFilterCalculate.functions";
import type { IFloorPayload } from "../../../components/FacilityManager/Domain/Types/FloorPlan/FloorPayload.type";
import type { BookingType } from "../../Booking-Form/typings/booking-inputs";
import {
  SelectedZoneOrWorkplaceType,
  type SelectedZoneOrWorkplace
} from "../../Reports/slices/report.slice";
import type { GroupFilterSelected } from "../../../components/BookingForm/BookingFilter/types/GroupFilters.type";
import type { FloorPlanMemo } from "../../Reports/typings/reports.types";

export type ManagementSelectionReportFilterType =
  | "location"
  | "floor"
  | SelectedZoneOrWorkplaceType;

export type ManagementSelectionReportParams = {
  filterIds: number[];
  filterType: ManagementSelectionReportFilterType;
  bookingType: BookingType;
  calcPerWorkplace: boolean;
};

type ResolveSelectionReportParamsArgs = {
  selectedZoneOrWorkplace: SelectedZoneOrWorkplace;
  selectedBookingType: BookingType;
  selectedAttributes: GroupFilterSelected[] | undefined;
  floorId: number;
  floorPlan: IFloorPayload | undefined;
  fallbackFilterType: SelectedZoneOrWorkplaceType;
  fallbackBookingType: BookingType;
  fallbackFilterIds: number[];
};

/** `getFetchValues` widens filterTypeVal to `string | undefined`. */
function toSelectionFilterType(
  value: string | undefined,
  fallback: SelectedZoneOrWorkplaceType
): ManagementSelectionReportFilterType {
  if (value === "location" || value === "floor") {
    return value;
  }
  if (
    value === SelectedZoneOrWorkplaceType.ZONE ||
    value === SelectedZoneOrWorkplaceType.WORKPLACE
  ) {
    return value;
  }
  return fallback;
}

/**
 * Resolves capacity-report filter params for an open place/zone selection.
 * Matches Capacity Calculate: zone + attributes → places inside the zone (intersection).
 */
export function resolveManagementSelectionReportParams({
  selectedZoneOrWorkplace,
  selectedBookingType,
  selectedAttributes,
  floorId,
  floorPlan,
  fallbackFilterType,
  fallbackBookingType,
  fallbackFilterIds
}: ResolveSelectionReportParamsArgs): ManagementSelectionReportParams {
  const hasAttributes = (selectedAttributes?.length ?? 0) > 0;
  if (!hasAttributes || !floorPlan) {
    return {
      filterIds: fallbackFilterIds,
      filterType: fallbackFilterType,
      bookingType: fallbackBookingType,
      calcPerWorkplace: true
    };
  }

  const floor = { id: floorId, open: true };
  const floorMemo: FloorPlanMemo = {
    [floorId]: { floorData: floorPlan, picturePath: floorPlan.outlineUrl ?? "" }
  };

  const { params, reportMode } = getFilterParams({
    selectedZoneOrWorkplace,
    selectedBookingType,
    floor,
    floorData: floorPlan,
    attFilterEntries: [],
    attributeSelected: selectedAttributes
  });

  const newAttFilterEntries = getFilterEntries(selectedZoneOrWorkplace, [], floorMemo, floor);

  const { filterTyp, bookingType } = getFilterAndBookingType(
    newAttFilterEntries,
    selectedBookingType,
    fallbackFilterType
  );

  const { calcPerWpVal, filterTypeVal } = getFetchValues(
    newAttFilterEntries,
    floor,
    selectedZoneOrWorkplace,
    reportMode,
    filterTyp
  );

  return {
    filterIds: params,
    filterType: toSelectionFilterType(filterTypeVal, fallbackFilterType),
    bookingType,
    calcPerWorkplace: calcPerWpVal
  };
}
